/**
 * ConverterPage Component
 *
 * Main page for media format conversion.
 * Supports single file and batch (multiple file) conversion.
 */

import { useState, useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { MediaUploader } from '../../components/common/MediaUploader';
import type { MediaType } from '../../components/common/MediaUploader';
import { ToastContainer } from '../../components/Toast';
import { TokenEstimateCard } from '../../components/tokens/TokenEstimateCard';
import { useMediaConversion } from '../../hooks/useMediaConversion';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../hooks/useTranslation';
import { ConverterHero } from './ConverterHero';
import { FileInfo } from './FileInfo';
import { ConversionSettings, type ConversionSettingsData } from './ConversionSettings';
import { clampQualityForMediaType, getDefaultOutputFormat } from './formats';
import { ConversionProgress } from './ConversionProgress';
import { ConversionResult } from './ConversionResult';
import { BatchConversionProgress } from './BatchConversionProgress';
import { BatchConversionResults } from './BatchConversionResults';
import type { BatchFile } from './types';

import { handleError } from '../../utils/errorHandler';
import {
  costGateBlockLabel,
  COST_GATE_OPEN,
  type CostGateStatus,
  type CostGate,
} from '../../utils/costGate';

// ============================================================================
// Types
// ============================================================================

type ConversionState = 'idle' | 'settings' | 'converting' | 'complete' | 'error';

// ============================================================================
// Helpers
// ============================================================================

let batchIdCounter = 0;

/** Lowercase extension including the dot (e.g. ".mp4"), or "" when absent. */
function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(dotIndex).toLowerCase() : '';
}

function createBatchFile(file: File, mediaType: 'audio' | 'video'): BatchFile {
  return {
    id: `batch-${++batchIdCounter}`,
    file,
    mediaType,
    status: 'pending',
    progress: 0,
    statusMessage: '',
    result: null,
    error: null,
  };
}

// ============================================================================
// Component
// ============================================================================

export const ConverterPage = memo(function ConverterPage() {
  const { t } = useTranslation();
  const { toasts, showToast, removeToast } = useToast();

  // State
  const [state, setState] = useState<ConversionState>('idle');
  const [batchFiles, setBatchFiles] = useState<BatchFile[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [settings, setSettings] = useState<ConversionSettingsData>({
    outputFormat: '.mp3',
    quality: 'medium',
    preserveMetadata: true,
    normalizeAudio: false,
  });
  const [costGate, setCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);
  const cancelledRef = useRef(false);

  // Single-file convenience getters
  const isBatchMode = batchFiles.length > 1;
  const currentFile = batchFiles[0] ?? null;

  // Majority media type — must match the rule used to pick the default output
  // format in handleMultipleFilesSelect, otherwise the format dropdown would
  // list audio formats while holding a video format as its value.
  const mediaType = useMemo<'audio' | 'video'>(() => {
    if (batchFiles.length === 0) return 'audio';
    const videoCount = batchFiles.filter((f) => f.mediaType === 'video').length;
    return videoCount >= batchFiles.length / 2 ? 'video' : 'audio';
  }, [batchFiles]);

  // Input extension shared by every selected file, "" when they differ.
  const inputExtension = useMemo(() => {
    const extensions = new Set(batchFiles.map((f) => getFileExtension(f.file.name)));
    return extensions.size === 1 ? [...extensions][0] : '';
  }, [batchFiles]);

  // Conversion hook
  const {
    convert,
    reset: resetConversion,
    progress,
    statusMessage,
    result,
    error,
  } = useMediaConversion();

  // Sync hook progress to current batch file during conversion
  useEffect(() => {
    if (state !== 'converting' || !isBatchMode) return;
    setBatchFiles((prev) =>
      prev.map((f, i) =>
        i === currentBatchIndex && f.status === 'converting' ? { ...f, progress, statusMessage } : f
      )
    );
  }, [progress, statusMessage, state, currentBatchIndex, isBatchMode]);

  // Estimate duration from file size
  const estimatedDurationSeconds = useMemo(() => {
    if (batchFiles.length === 0) return 0;
    const totalSize = batchFiles.reduce((sum, f) => sum + f.file.size, 0);
    const bytesPerSecond = mediaType === 'video' ? 500 * 1024 : 16 * 1024;
    return Math.max(10, Math.min(totalSize / bytesPerSecond, 7200));
  }, [batchFiles, mediaType]);

  // Token estimate parameters
  // Only what prices the conversion. `media_type` was sent here and read by
  // nobody — the codec and the resolution come from output_format/quality —
  // and POST /api/v1/cost/preview now refuses a name it does not price rather
  // than quoting around it.
  const estimateParams = useMemo(
    () => ({
      duration_seconds: estimatedDurationSeconds,
      output_format: settings.outputFormat.replace('.', ''),
      quality: settings.quality,
    }),
    [estimatedDurationSeconds, settings.outputFormat, settings.quality]
  );

  const handleEstimateChange = useCallback(({ status }: CostGate) => {
    setCostGate(status);
  }, []);

  // Single file selected
  const handleFileSelect = useCallback((selectedFile: File, detectedMediaType: MediaType) => {
    const type = detectedMediaType === 'image' ? 'audio' : detectedMediaType;
    setBatchFiles([createBatchFile(selectedFile, type)]);
    setSettings((prev) => ({
      ...prev,
      outputFormat: getDefaultOutputFormat(type, getFileExtension(selectedFile.name)),
      quality: clampQualityForMediaType(prev.quality, type),
    }));
    setState('settings');
  }, []);

  // Multiple files selected
  const handleMultipleFilesSelect = useCallback(
    (files: Array<{ file: File; mediaType: MediaType }>) => {
      const batch = files.map((f) => {
        const type = f.mediaType === 'image' ? ('audio' as const) : f.mediaType;
        return createBatchFile(f.file, type);
      });
      setBatchFiles(batch);
      // Set default format based on majority media type. With mixed inputs
      // there is no single "input extension" to avoid, so the canonical format
      // is used and the per-file recompression warning is not shown.
      const videoCount = batch.filter((f) => f.mediaType === 'video').length;
      const batchType = videoCount >= batch.length / 2 ? 'video' : 'audio';
      const extensions = new Set(batch.map((f) => getFileExtension(f.file.name)));
      const sharedExtension = extensions.size === 1 ? [...extensions][0] : '';
      setSettings((prev) => ({
        ...prev,
        outputFormat: getDefaultOutputFormat(batchType, sharedExtension),
        quality: clampQualityForMediaType(prev.quality, batchType),
      }));
      setState('settings');
    },
    []
  );

  // Single-file conversion
  const handleConvertSingle = useCallback(async () => {
    if (!currentFile) return;

    setState('converting');
    try {
      await convert(currentFile.file, settings);
      setState('complete');
      showToast(
        'success',
        t('converter.success.title', { defaultValue: 'Conversion Complete' }),
        t('converter.success.message', {
          defaultValue: 'Your file has been converted successfully!',
        })
      );
    } catch (err: unknown) {
      setState('error');
      const errorMessage = handleError(err, 'Index', 'Conversion failed');
      showToast(
        'error',
        t('converter.errors.title', { defaultValue: 'Conversion Failed' }),
        errorMessage
      );
    }
  }, [currentFile, settings, convert, showToast, t]);

  // Batch conversion - process files sequentially
  const handleConvertBatch = useCallback(async () => {
    setState('converting');
    cancelledRef.current = false;
    setCurrentBatchIndex(0);

    for (let i = 0; i < batchFiles.length; i++) {
      if (cancelledRef.current) break;

      setCurrentBatchIndex(i);

      // Mark current file as converting
      setBatchFiles((prev) =>
        prev.map((f, idx) =>
          idx === i ? { ...f, status: 'converting', progress: 0, statusMessage: 'Starting...' } : f
        )
      );

      try {
        resetConversion();
        // convert() returns the per-file result directly, so each batch entry
        // gets its OWN download URL / output path (no shared-state broadcast).
        const fileResult = await convert(batchFiles[i].file, settings);

        setBatchFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? {
                  ...f,
                  status: 'complete',
                  progress: 100,
                  statusMessage: 'Complete!',
                  result: fileResult,
                }
              : f
          )
        );
      } catch (err: unknown) {
        const errorMessage = handleError(err, 'BatchConversion', 'Conversion failed');
        setBatchFiles((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: errorMessage } : f))
        );
      }
    }

    setState('complete');
    showToast(
      'success',
      t('converter.batch.completeToast', { defaultValue: 'Batch conversion complete!' }),
      t('converter.batch.completeMessage', {
        defaultValue: 'Processed {{count}} files',
        count: batchFiles.length,
      })
    );
  }, [batchFiles, settings, convert, resetConversion, showToast, t]);

  const handleConvert = isBatchMode ? handleConvertBatch : handleConvertSingle;

  const handleReset = useCallback(() => {
    setBatchFiles([]);
    setState('idle');
    setCurrentBatchIndex(0);
    cancelledRef.current = false;
    resetConversion();
  }, [resetConversion]);

  const handleCancel = useCallback(() => {
    setBatchFiles([]);
    setState('idle');
    setCurrentBatchIndex(0);
  }, []);

  const handleRemoveFile = useCallback((id: string) => {
    setBatchFiles((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      if (updated.length === 0) {
        setState('idle');
      }
      return updated;
    });
  }, []);

  return (
    <main className="page-container" data-testid="converter-page">
      {/* Hero Section - Only show when idle */}
      {state === 'idle' && <ConverterHero />}

      {/* Main Content */}
      <div className="space-y-6">
        {/* Upload Section */}
        {state === 'idle' && (
          <MediaUploader
            accept={['audio', 'video']}
            onFileSelect={handleFileSelect}
            onMultipleFilesSelect={handleMultipleFilesSelect}
            multiple
            maxSizeMB={500}
            translationNamespace="converter"
            dropzoneText={t('converter.dropzone.title', {
              defaultValue: 'Drag & drop your files here',
            })}
          />
        )}

        {/* Settings Section */}
        {state === 'settings' && batchFiles.length > 0 && (
          <>
            {/* File list */}
            {isBatchMode ? (
              <div className="p-4 sm:p-6 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
                    {t('converter.batch.selectedFiles', {
                      defaultValue: '{{count}} files selected',
                      count: batchFiles.length,
                    })}
                  </h3>
                </div>
                <div className="space-y-2">
                  {batchFiles.map((bf) => (
                    <div
                      key={bf.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-secondary-50 dark:bg-secondary-700/50"
                    >
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          bf.mediaType === 'video'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        }`}
                      >
                        {bf.mediaType === 'video' ? 'Video' : 'Audio'}
                      </span>
                      <span className="text-sm text-secondary-900 dark:text-white truncate flex-1">
                        {bf.file.name}
                      </span>
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">
                        {(bf.file.size / (1024 * 1024)).toFixed(1)} MB
                      </span>
                      <button
                        onClick={() => handleRemoveFile(bf.id)}
                        className="text-secondary-400 hover:text-red-500 transition-colors p-1"
                        aria-label="Remove file"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <FileInfo file={currentFile!.file} mediaType={mediaType} />
            )}

            <ConversionSettings
              settings={settings}
              onChange={setSettings}
              mediaType={mediaType}
              inputExtension={inputExtension}
            />

            {/* Token Estimate */}
            {estimatedDurationSeconds > 0 && (
              <TokenEstimateCard
                operationPath="/api/v1/convert/video"
                parameters={estimateParams}
                onEstimateChange={handleEstimateChange}
              />
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={handleCancel}
                className="flex-1 px-6 py-3 rounded-lg border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 font-medium transition-colors"
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={handleConvert}
                disabled={costGate !== 'affordable'}
                className="flex-1 px-6 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-colors"
              >
                {costGateBlockLabel(costGate, t) ??
                  (isBatchMode
                    ? t('converter.batch.convertAll', {
                        defaultValue: 'Convert {{count}} Files',
                        count: batchFiles.length,
                      })
                    : t('converter.convert', { defaultValue: 'Convert' }))}
              </button>
            </div>
          </>
        )}

        {/* Progress Section */}
        {state === 'converting' && (
          <>
            {isBatchMode ? (
              <BatchConversionProgress
                files={batchFiles}
                currentIndex={currentBatchIndex}
                outputFormat={settings.outputFormat}
              />
            ) : (
              <ConversionProgress
                fileName={currentFile?.file.name}
                progress={progress}
                statusMessage={statusMessage}
                outputFormat={settings.outputFormat}
              />
            )}
          </>
        )}

        {/* Result Section */}
        {state === 'complete' && (
          <>
            {isBatchMode ? (
              <BatchConversionResults files={batchFiles} onConvertMore={handleReset} />
            ) : (
              result && (
                <ConversionResult
                  result={result}
                  mediaType={mediaType}
                  onConvertAnother={handleReset}
                />
              )
            )}
          </>
        )}

        {/* Error Section (single file only - batch handles errors per-file) */}
        {state === 'error' && !isBatchMode && (
          <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('converter.tryAgain', { defaultValue: 'Try Again' })}
            </button>
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </main>
  );
});

export default ConverterPage;
